/**
 * A user's roles in one network, as the user page edits them. Per application
 * a user holds at most one network role, plus a role on any team or team
 * group. Which team roles a team accepts depends on the team's type — the
 * same rules the old UI applies.
 */

import type { Network } from '@/services/networks/networksApi'
import type { Role } from '@/services/users/rolesApi'
import type { RoleRef, UserRole } from '@/services/users/usersApi'

/** Per application: the network role and the role on each team and team group. */
export interface AppRolesDraft {
  /** Role id, or '' when the user is not a user of the application. */
  network: string
  /** Team id → role id. */
  teams: Record<string, string>
  /** Team group id → role id. */
  teamGroups: Record<string, string>
}

export type RolesDraft = Record<string, AppRolesDraft>

/** The applications the network runs, which each have their own roles. */
export function networkApps(network: Network | null): string[] {
  const apps: string[] = []
  if (network?.advertisingPlatform) apps.push('ADVERTISING')
  if (network?.dataPlatform) apps.push('DATA')
  if (network?.politicalApp) apps.push('POLITICAL')
  return apps
}

export const emptyAppRoles = (): AppRolesDraft => ({ network: '', teams: {}, teamGroups: {} })

const has = (role: Role, permission: string) => (role.permissions ?? []).includes(permission)

/** The network roles of an application. */
export function networkRolesFor(app: string, catalog: Role[]): Role[] {
  return catalog.filter((r) => r.scope === 'NETWORK' && r.application === app)
}

/** The roles a team or team group of the given type can be given in an application. */
export function teamRolesFor(app: string, teamType: string | undefined, catalog: Role[]): Role[] {
  const teamRoles = catalog.filter((r) => r.scope === 'TEAM' && r.application === app)
  if (app === 'ADVERTISING') {
    const marketplace = [
      'SELF_SERVICE_OWN_ADVERTISING',
      'AX_ADVERTISER',
      'AX_PUBLISHER',
      'CREATIVE_AGENCY',
    ]
    switch (teamType) {
      case 'AX_ADVERTISER':
        return teamRoles.filter(
          (r) =>
            (!has(r, 'SELF_SERVICE_OWN_ADVERTISING') && !has(r, 'CREATIVE_AGENCY')) ||
            has(r, 'AX_ADVERTISER') ||
            has(r, 'AX_PUBLISHER')
        )
      case 'AX_PUBLISHER':
        return teamRoles.filter(
          (r) =>
            (!has(r, 'AX_ADVERTISER') &&
              !has(r, 'SELF_SERVICE_OWN_ADVERTISING') &&
              !has(r, 'CREATIVE_AGENCY')) ||
            has(r, 'AX_PUBLISHER')
        )
      case 'SUI':
        return teamRoles.filter(
          (r) =>
            (!has(r, 'AX_ADVERTISER') && !has(r, 'AX_PUBLISHER')) ||
            has(r, 'SELF_SERVICE_OWN_ADVERTISING')
        )
      case 'CREATIVE_AGENCY':
        return teamRoles.filter(
          (r) => (!has(r, 'AX_ADVERTISER') && !has(r, 'AX_PUBLISHER')) || has(r, 'CREATIVE_AGENCY')
        )
      default:
        return teamRoles.filter((r) => !marketplace.some((p) => has(r, p)))
    }
  }
  if (app === 'POLITICAL') {
    if (teamType === 'SPONSOR') return teamRoles.filter((r) => !has(r, 'CONTROLLER'))
    if (teamType === 'CONTROLLER') return teamRoles.filter((r) => !has(r, 'SPONSOR'))
  }
  return teamRoles
}

/** Whether the app's network role lets the user be given roles on team groups. */
export function canHoldTeamGroupRoles(
  app: string,
  networkRoleId: string,
  catalog: Role[]
): boolean {
  const role = catalog.find((r) => r.id === networkRoleId)
  if (!role) return false
  return has(role, 'MANAGE_SYSTEM') || (app === 'POLITICAL' && has(role, 'POLITICAL_ADMIN'))
}

function appOf(role: UserRole, catalog: Role[]): string | undefined {
  return catalog.find((r) => r.id === role.role.id)?.application ?? role.role.application
}

/** The user's roles in the network, laid out per application. */
export function rolesToDraft(roles: UserRole[], apps: string[], catalog: Role[]): RolesDraft {
  const draft: RolesDraft = Object.fromEntries(apps.map((app) => [app, emptyAppRoles()]))
  for (const r of roles) {
    const app = appOf(r, catalog)
    if (!app || !draft[app]) continue
    if (r.team) draft[app].teams[r.team.id] = r.role.id
    else if (r.teamGroup) draft[app].teamGroups[r.teamGroup.id] = r.role.id
    else if (!draft[app].network) draft[app].network = r.role.id
  }
  return draft
}

/**
 * The roles to save. The API replaces every role in the network, so roles the
 * editor does not show — another application's, or on a team or group it did
 * not list — are sent back as they were.
 */
export function draftToRoles(
  draft: RolesDraft,
  existing: UserRole[],
  catalog: Role[],
  teamIds: Set<string>,
  teamGroupIds: Set<string>
): RoleRef[] {
  const kept: RoleRef[] = existing
    .filter((r) => {
      const app = appOf(r, catalog)
      if (!app || !draft[app]) return true
      if (r.team) return !teamIds.has(r.team.id)
      if (r.teamGroup) return !teamGroupIds.has(r.teamGroup.id)
      return false
    })
    .map((r) => ({
      role: r.role.id,
      ...(r.team ? { team: r.team.id } : {}),
      ...(r.teamGroup ? { teamGroup: r.teamGroup.id } : {}),
    }))

  const edited: RoleRef[] = Object.values(draft).flatMap((app) => [
    ...(app.network ? [{ role: app.network }] : []),
    ...Object.entries(app.teams)
      .filter(([id, role]) => role && teamIds.has(id))
      .map(([team, role]) => ({ role, team })),
    ...Object.entries(app.teamGroups)
      .filter(([id, role]) => role && teamGroupIds.has(id))
      .map(([teamGroup, role]) => ({ role, teamGroup })),
  ])
  return [...kept, ...edited]
}
